import formatters from '../shared/formatters';

// Warm amber (never red) signals a data-quality flag without implying the client is in a
// bad state. Ordinary zero-hour clients stay on the neutral palette.
export const FLAG_ROW_BACKGROUND = '#FBF4EA';
export const FLAG_ACCENT_COLOR = '#B57A2E';

// Summary card grid: collapses responsively from six across down to one.
export const summaryGridColumns = {
  xs: '1fr',
  sm: 'repeat(2, minmax(0, 1fr))',
  md: 'repeat(3, minmax(0, 1fr))',
  lg: 'repeat(6, minmax(0, 1fr))',
};

// Column definitions. `actual: true` marks the columns that only make sense once a period
// has started, so the next month can drop them rather than show fabricated zeros.
const ALL_COLUMNS = [
  {key: 'client', sortKey: 'clientName', align: 'left', headerExpression: 'metrics.ClientMetrics.ClientMonthly.Table.Client'},
  {key: 'scheduledRate', sortKey: 'scheduledRate', align: 'right', headerExpression: 'metrics.ClientMetrics.ClientMonthly.Table.ScheduledRate'},
  {key: 'scheduledHours', sortKey: 'scheduledHours', align: 'right', headerExpression: 'metrics.ClientMetrics.ClientMonthly.Table.ScheduledHours'},
  {
    key: 'scheduledWeightedHours',
    sortKey: 'scheduledWeightedHours',
    align: 'right',
    headerExpression: 'metrics.ClientMetrics.ClientMonthly.Table.ScheduledWeightedHours',
  },
  {
    key: 'scheduledRevenue',
    sortKey: 'scheduledRevenue',
    align: 'right',
    headerExpression: 'metrics.ClientMetrics.ClientMonthly.Table.ScheduledRevenue',
  },
  {key: 'actualHours', sortKey: 'actualHours', align: 'right', actual: true, headerExpression: 'metrics.ClientMetrics.ClientMonthly.Table.ActualHours'},
  {key: 'actualRevenue', sortKey: 'actualRevenue', align: 'right', actual: true, headerExpression: 'metrics.ClientMetrics.ClientMonthly.Table.ActualRevenue'},
  {key: 'status', sortKey: null, align: 'left', headerExpression: 'metrics.ClientMetrics.ClientMonthly.Table.Status'},
];

// Next month has no actuals, so its columns are omitted entirely (never rendered as $0).
export const getTableColumns = (showActual) => {
  return showActual ? ALL_COLUMNS : ALL_COLUMNS.filter((column) => !column.actual);
};

export const DEFAULT_SORT = {key: 'scheduledRevenue', direction: 'desc'};

export const hasMissingRate = (client) => {
  return Boolean(client?.missingScheduledRate) || formatters.isMissingValue(client?.scheduledRate);
};

export const getUnpricedHours = (client) => {
  return formatters.isMissingValue(client?.unpricedScheduledHours) ? 0 : client.unpricedScheduledHours;
};

export const isFlaggedClient = (client) => {
  return hasMissingRate(client) || getUnpricedHours(client) > 0;
};

// Missing numbers (e.g. clients without a rate) always sink to the bottom regardless of
// direction; ties break on clientRn so the order stays stable as more pages append.
export const compareClients = (left, right, sortKey, sortDirection) => {
  const directionMultiplier = sortDirection === 'asc' ? 1 : -1;
  const tiebreak = (left.clientRn || '').localeCompare(right.clientRn || '');

  if (sortKey === 'clientName') {
    const comparison = (left.clientName || '').localeCompare(right.clientName || '', undefined, {sensitivity: 'base'});

    return comparison !== 0 ? directionMultiplier * comparison : tiebreak;
  }

  const leftValue = left?.[sortKey];
  const rightValue = right?.[sortKey];
  const leftMissing = formatters.isMissingValue(leftValue);
  const rightMissing = formatters.isMissingValue(rightValue);

  if (leftMissing && rightMissing) {
    return tiebreak;
  }

  if (leftMissing) {
    return 1;
  }

  if (rightMissing) {
    return -1;
  }

  return leftValue !== rightValue ? directionMultiplier * (leftValue - rightValue) : tiebreak;
};
